import type { CalendarEventDto } from '@lib/dto/venue-calendar.dto'
import type { CalendarDay } from '@lib/validators/temporal.validator'

/** Why an event is on the list, most pressing first. */
export const ATTENTION_REASONS = ['no-plans', 'unconfirmed', 'no-times'] as const

export type AttentionReason = (typeof ATTENTION_REASONS)[number]

export interface AttentionItem {
  eventId: string
  name: string
  /** The event's day, so the list can say when without re-deriving it. */
  dayKey: CalendarDay
  reason: AttentionReason
}

/** What the dashboard says about each reason. One line, and it names the fix. */
export const ATTENTION_LABELS: Record<AttentionReason, string> = {
  'no-plans': 'No floor plan yet',
  unconfirmed: 'Plans not confirmed',
  'no-times': 'No start time set',
}

/**
 * The loose ends worth a venue's attention, soonest first.
 *
 * Only what is still ahead. A past event's plans are frozen by the date and can
 * no longer be confirmed, and its missing start time is a fact about something
 * that already happened — a list that asks for impossible work is a list people
 * learn to skip.
 *
 * One line per event, carrying the most pressing thing wrong with it: an event
 * with no plan at all is further from ready than one whose plans merely await
 * confirmation, and three lines about one wedding would crowd out the other four
 * weddings that also need something.
 */
export function attentionItems(
  events: CalendarEventDto[],
  today: string,
  limit = 6,
): AttentionItem[] {
  const items: AttentionItem[] = []
  for (const event of events) {
    const dayKey = event.date
    if (dayKey < today) continue
    const reason = reasonFor(event)
    if (reason === null) continue
    items.push({ eventId: event.id, name: event.name, dayKey, reason })
  }
  items.sort(
    (a, b) =>
      a.dayKey.localeCompare(b.dayKey) ||
      ATTENTION_REASONS.indexOf(a.reason) - ATTENTION_REASONS.indexOf(b.reason) ||
      a.name.localeCompare(b.name),
  )
  return items.slice(0, limit)
}

function reasonFor(event: CalendarEventDto): AttentionReason | null {
  if (event.phaseCount === 0) return 'no-plans'
  if (event.unconfirmedPhaseCount > 0) return 'unconfirmed'
  if (event.startsAt === null) return 'no-times'
  return null
}

/**
 * The next events at this venue, from today forward.
 *
 * Ordered by day and then by start time, with untimed events last within a day:
 * they have no place in a sequence, and guessing one would put a wedding before a
 * tasting because of how the rows happened to arrive.
 */
export function upcoming(events: CalendarEventDto[], today: string, limit = 5): CalendarEventDto[] {
  return events
    .filter((event) => event.date >= today)
    .sort((a, b) => a.date.localeCompare(b.date) || byStartThenName(a, b))
    .slice(0, limit)
}

/**
 * Timed events in time order, then the untimed ones.
 *
 * The null is handled explicitly rather than by substituting a high character:
 * `'~'.localeCompare('2026-…')` is negative, because collation sorts punctuation
 * before digits — so the "sorts last" sentinel sorts first, and an untimed tasting
 * leads a day it should trail.
 */
export function byStartThenName(
  a: Pick<CalendarEventDto, 'startsAt' | 'name'>,
  b: Pick<CalendarEventDto, 'startsAt' | 'name'>,
): number {
  if (a.startsAt === null && b.startsAt !== null) return 1
  if (a.startsAt !== null && b.startsAt === null) return -1
  const byTime = (a.startsAt ?? '').localeCompare(b.startsAt ?? '')
  return byTime !== 0 ? byTime : a.name.localeCompare(b.name)
}
